import type { SelectedAddressBookItems } from "~/features/quote/context/quote-address-context.tsx";
import { defaultAddressBookItem } from "~/features/quote/context/quote-address-context.tsx";
import type { CarrierPrice } from "~/shared/schemas/carrier-schema.ts";
import type { CustomerDetails } from "~/shared/schemas/customer-schema.ts";
import type { ZipCode } from "~/shared/schemas/location-state-schema.ts";
import type {
	HandlingUnit,
	QuoteStateType,
} from "~/shared/types/quote-details-types.ts";

import type { AugmentedShipment } from "../types/augmented-shipment";

// The generate-documents flow (`TenderModal`/`use-tender-modal`) is built for
// the create-quote `QuoteDetailsContext`/`QuoteAddressContext`, which shipment
// edit doesn't have. Only a handful of fields from `quoteState` and
// `selectedAddressBookItems` are actually read by that component tree (see
// `buildShippingLabelReportBody`, `ConfirmationScreen`, `TenderModal`) — every
// other field below is an unused placeholder required only to satisfy the
// `QuoteStateType` shape.
const buildZipCode = (
	city: string | null | undefined,
	stateAbbreviation: string | null | undefined,
	postalCode: string | null | undefined,
): ZipCode => ({
	id: "",
	postalCode: postalCode ?? "",
	latitude: null,
	longitude: null,
	city: {
		name: city ?? "",
		stateId: "",
		state: {
			id: "",
			abbreviation: stateAbbreviation ?? "",
			countryCode: "US",
			country: { abbreviation: "US", name: "United States" },
			name: "",
		},
		type: null,
	},
});

const buildCarrierPrice = (carrierName: string | null): CarrierPrice => ({
	id: 0,
	quoteId: 0,
	carrierId: 0,
	carrier: {
		id: 0,
		name: carrierName ?? "",
		displayName: carrierName ?? "",
		tollFreeNumber: "",
		isPreferred: false,
		isCarrierOfTheYear: false,
		isEconomy: false,
		isPartials: false,
		scac: null,
	},
	carrierRate: 0,
	customerRate: 0,
	distributorRate: null,
	brokerMargin: 0,
	carrierQuoteId: null,
	carrierQuoteExpirationDate: "",
	carrierPriceTypeId: 0,
	carrierPriceType: null,
	serviceLevelId: 0,
	serviceLevel: { id: 0, name: "" },
	serviceAreaTypeId: null,
	serviceType: "",
	serviceLevelDescription: "",
	destinationTerminalCode: null,
	originTerminalCode: null,
	transitDays: null,
	maxLiabilityOverride: null,
	maxLiabilityNew: 0,
	maxLiabilityUsed: 0,
	carrierTotalAccessorialCharges: null,
	carrierTotalAccessorialFuelSurcharges: null,
	customerTotalAccessorialCharges: null,
	customerTotalAccessorialFuelSurcharges: null,
	priceCharges: null,
});

const buildHandlingUnit = (quantity: number): HandlingUnit => ({
	id: 0,
	name: "",
	description: "",
	quantity,
	unit: { id: 0, name: "", code: "" },
	weight: null,
	density: 0,
	dimensionLength: null,
	dimensionWidth: null,
	dimensionHeight: null,
	class: { id: 0, name: "", code: "" },
	nmfc: "",
	isHazmat: false,
	isStackable: false,
	pieceCount: null,
	width: null,
	height: null,
	length: null,
	estClass: 0,
});

const buildCustomerDetails = (customerId: number): CustomerDetails => ({
	id: customerId,
	name: "",
	hasSignedLTLAddendum: false,
	status: { id: 0, name: "" },
	primaryBroker: null,
	ltlBroker: null,
});

type DocumentsStop = {
	companyName?: string | null;
	address1?: string | null;
	address2?: string | null;
	city?: string | null;
	stateProvince?: string | null;
	postalCode?: string | null;
	country?: string | null;
};

export type DocumentsContextSource = {
	quoteId: number;
	customerId: number;
	poNumber: number;
	customerReference: string;
	carrierName: string | null;
	totalQuantity: number;
	pickup: DocumentsStop;
	delivery: DocumentsStop;
};

/**
 * Builds a `QuoteStateType` that carries only the real values the tender/generate
 * document flow reads, so `TenderModal` can be reused outside create-quote.
 */
export const buildDocumentsQuoteState = ({
	quoteId,
	customerId,
	poNumber,
	customerReference,
	carrierName,
	totalQuantity,
	pickup,
	delivery,
}: DocumentsContextSource): QuoteStateType => {
	return {
		id: quoteId,
		publicGuid: "",
		templateId: null,
		templateName: null,
		addtDetails: [],
		pickupCountry: { abbreviation: "US", name: "United States" },
		deliveryCountry: { abbreviation: "US", name: "United States" },
		shipmentDate: "",
		customer: buildCustomerDetails(customerId),
		customerAr: { id: 0, name: "", email: null, mode: "", isBackup: false },
		pickupPostalCode: buildZipCode(
			pickup.city,
			pickup.stateProvince,
			pickup.postalCode,
		),
		deliveryPostalCode: buildZipCode(
			delivery.city,
			delivery.stateProvince,
			delivery.postalCode,
		),
		items: [buildHandlingUnit(totalQuantity)],
		totalLinearFeet: 0,
		customTotalLinearFeet: 0,
		totalCubicFootage: 0,
		totalDensity: 0,
		deliveryServices: [],
		pickupServices: [],
		poNumber,
		poNumbers: String(poNumber),
		soNumber: "",
		specialInstructions: "",
		customerReference,
		isGuest: false,
		pieceCaseCount: null,
		totalWeight: 0,
		totalQuantity,
		class: "",
		carrierPrices: [buildCarrierPrice(carrierName)],
		eTag: "",
		createdDate: "",
		expirationDate: "",
		totalCubicFeet: 0,
		selectedCarrierPriceId: 0,
		measuringType: "LB/IN",
		customerDetails: buildCustomerDetails(customerId),
		customerCredits: {},
		insuranceValue: null,
		insuranceApplyTypeId: null,
		insuranceProductTypeId: null,
		puNumber: null,
		deliveryPO: null,
		hasRoutingGuideMatch: false,
		billingCustomerId: null,
	};
};

const buildAddressBookItem = (stop: DocumentsStop) => ({
	...defaultAddressBookItem,
	companyName: stop.companyName ?? null,
	address1: stop.address1 ?? "",
	address2: stop.address2 ?? "",
	postalCode: stop.postalCode ?? "",
	city: stop.city ?? "",
	stateProvince: stop.stateProvince ?? "",
	country: stop.country ?? "",
});

/**
 * Builds `SelectedAddressBookItems` so `buildShippingLabelReportBody` can read
 * companyName/address1/address2 for the pickup and delivery stops.
 */
export const buildDocumentsAddressBookItems = ({
	pickup,
	delivery,
}: Pick<
	DocumentsContextSource,
	"pickup" | "delivery"
>): SelectedAddressBookItems => ({
	pickup: buildAddressBookItem(pickup),
	delivery: buildAddressBookItem(delivery),
});

/**
 * Builds a `QuoteStateType` from a shipment so `TenderModal` can be reused
 * as-is for shipment edit.
 */
export const buildShipmentQuoteState = (
	shipment: AugmentedShipment,
	poNumber: number,
): QuoteStateType => {
	const { pickup, delivery, additionalDetails } = shipment.shipmentDetails;
	const totalQuantity = Math.max(
		1,
		shipment.shipmentDetails.freightDetails.lineItems.reduce(
			(acc, item) => acc + (Number(item.pieceCount) || 0),
			0,
		),
	);

	return buildDocumentsQuoteState({
		quoteId: shipment.quoteId ?? 0,
		customerId: shipment.customer.id ?? 0,
		poNumber,
		customerReference: additionalDetails.customerReference ?? "",
		carrierName: shipment.carrier.carrierName,
		totalQuantity,
		pickup,
		delivery,
	});
};

/**
 * Builds `SelectedAddressBookItems` from the shipment's pickup/delivery stops.
 */
export const buildShipmentAddressBookItems = (
	shipment: AugmentedShipment,
): SelectedAddressBookItems =>
	buildDocumentsAddressBookItems(shipment.shipmentDetails);
